import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User) => void;
  setToken: (token: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        
        try {
          // For local development only, simulate login
          if (import.meta.env.DEV && window.location.hostname === 'localhost') {
            await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
            
            const mockUser: User = {
              id: 'mock_user_1',
              email: email,
              firstName: 'Test',
              lastName: 'User'
            };
            
            const mockToken = 'mock_token_' + Date.now();
            
                      set({
            user: mockUser,
            token: mockToken,
            isAuthenticated: true,
            isLoading: false
          });
          
          // Also save token to localStorage for API client
          localStorage.setItem('auth_token', mockToken);
            return;
          }

          // In production, make actual API call
          const { authApi } = await import('@/services/api');
          const response = await authApi.login({ email, password });
          
          // Login response structure: {success, timestamp, data: {message, user, tokens}}
          const loginData = response as any; // Type assertion since login endpoint has different structure
          
          set({
            user: loginData.data.user,
            token: loginData.data.tokens.idToken,
            isAuthenticated: true,
            isLoading: false
          });
          
          // Also save token to localStorage for API client
          localStorage.setItem('auth_token', loginData.data.tokens.idToken);
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        console.log('🔐 [AUTH] Logging out user - clearing all data');
        
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false
        });
        
        // Clear all authentication data from localStorage
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth-storage');
        
        console.log('✅ [AUTH] User logged out successfully');
      },

      setUser: (user: User) => {
        set({ user, isAuthenticated: true });
      },

      setToken: (token: string) => {
        set({ token });
        // Also save token to localStorage for API client
        localStorage.setItem('auth_token', token);
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
); 